import { trackFormError } from "utils/analytics/trackEvent";
import { useCallback, useEffect, useState } from "react";
import {
  useCreateHealthApplicationMutation,
  useGetHealthAppStepQuery,
  useUpdateStepMutation,
} from "types/generated/operations";
import Error from "uicomponents/Error";
import Loader from "uicomponents/Loader";
import {
  getProspectCanonicalId,
  getProspectUuid,
  getMemberUuid,
} from "utils/idHelpers";
import { getAppType } from "utils/helper";
import HealthInsuranceApplication from "./HealthInsuranceApplication";
import { isTestUser } from "utils/helper";

const HealthInsuranceApplicationModel = () => {
  const appType = getAppType();
  const { data, error, loading, refetch } = useGetHealthAppStepQuery({
    variables: { uuid: getProspectUuid(), appType: appType },
    fetchPolicy: "no-cache",
    skip: !getProspectUuid() && !isTestUser,
  });

  const hasNoCobraInfo =
    data?.prospect?.applications?.[0]?.step === 8 &&
    data?.prospect?.healthInsuranceInformation?.cobraUsage &&
    data?.prospect?.cobraCensuses?.length === 0;
  const hasNoHealthInfo =
    data?.prospect?.applications?.[0]?.step === 8 &&
    data?.prospect?.healthInsuranceInformation?.currentlyOfferHealthInsurance &&
    data.prospect?.applications?.[0]?.documents?.length === 0;
  const hasSelectedNoForCobra =
    data?.prospect?.applications?.[0]?.step === 7 &&
    !data?.prospect?.healthInsuranceInformation?.cobraUsage;

  const [createHealthApplication, { loading: createApplicationLoading }] =
    useCreateHealthApplicationMutation();
  const [updateStep] = useUpdateStepMutation();
  const [hasInitializedApplication, setHasInitializedApplication] =
    useState(false);

  const updateApplicationStep = useCallback(async () => {
    try {
      const decrementStepNumber = hasNoHealthInfo ? 6 : 7;
      const stepNumber = hasSelectedNoForCobra ? 8 : decrementStepNumber;
      await updateStep({
        variables: {
          uuid: data?.prospect?.applications?.[0].uuid || "",
          step: stepNumber,
        },
      });

      refetch();
    } catch (error: any) {
      trackFormError(error, `error deprecating ${appType} step`);

      return <Error />;
    }
  }, [
    updateStep,
    refetch,
    data,
    hasNoHealthInfo,
    hasSelectedNoForCobra,
    appType,
  ]);

  const getProspectData = useCallback(async () => {
    try {
      await createHealthApplication({
        variables: {
          prospectCanonicalId: getProspectCanonicalId(),
          prospectId: getProspectUuid(),
          appType: appType,
          memberUuid: getMemberUuid(),
        },
      });

      // Initialization is idempotent, and does not necessarily mean that the application was created if one already exists
      // It will, however, always trigger a prospect sync with clockwork
      setHasInitializedApplication(true);
      refetch();
    } catch (error: any) {
      trackFormError(error, `error creating ${appType}`);

      return <Error />;
    }
  }, [createHealthApplication, refetch, appType]);

  useEffect(() => {
    if (!hasInitializedApplication && !loading && !error) {
      getProspectData();
    }
    if (hasNoCobraInfo || hasNoHealthInfo || hasSelectedNoForCobra) {
      updateApplicationStep();
    }
  }, [
    hasInitializedApplication,
    error,
    loading,
    getProspectData,
    updateApplicationStep,
    hasNoCobraInfo,
    hasNoHealthInfo,
    hasSelectedNoForCobra,
  ]);

  if (loading || createApplicationLoading) {
    return <Loader />;
  }

  if (error) {
    trackFormError(error, `error getting ${appType} status`);
    return <Error />;
  }

  return !hasNoCobraInfo &&
    !hasNoHealthInfo &&
    data?.prospect?.applications?.length ? (
    <HealthInsuranceApplication data={data?.prospect} />
  ) : (
    <Loader />
  );
};

export default HealthInsuranceApplicationModel;
